import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  IconButton,
  Alert,
  Chip,
} from "@mui/material";
import { Close, PictureAsPdf, EventNote } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { getAllInputs, getFilterByRolls } from "../functions";
import InputsReportPdf from "./InputsReportPdf";

const SX_BTN_OUTLINE = {
  borderColor: "#f43f5e",
  color: "#f43f5e",
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  fontSize: "0.72rem",
  "&:hover": { borderColor: "#e11d48", background: "rgba(244,63,94,0.04)" },
};

const InputsReportModal = ({ open, onClose }) => {
  const [reportType, setReportType] = useState("active");
  const [dateFrom, setDateFrom]     = useState(moment().startOf("month"));
  const [dateTo,   setDateTo]       = useState(moment().endOf("month"));
  const [rolls,    setRolls]        = useState(null);   // null = no generado aún
  const [loading,  setLoading]      = useState(false);
  const [error,    setError]        = useState(null);

  const resetResult = () => setRolls(null);

  /* ── Filtrar y generar ── */
  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setRolls(null);
    try {
      // 1. Obtener todas las entradas y filtrar por rango de fecha
      const allInputs = await getAllInputs();
      const from = moment(dateFrom).startOf("day");
      const to   = moment(dateTo).endOf("day");

      const filteredInputs = (allInputs || []).filter((input) => {
        if (!input.date) return false;
        const d = moment(input.date);
        return d.isSameOrAfter(from) && d.isSameOrBefore(to);
      });

      if (filteredInputs.length === 0) {
        setRolls([]);
        return;
      }

      // 2. Obtener rollos de cada entrada filtrada en paralelo
      const rollsArrays = await Promise.all(
        filteredInputs.map((input) => getFilterByRolls(input.id))
      );

      // 3. Aplanar y enriquecer cada rollo con la fecha e id de su entrada
      const allRolls = rollsArrays.flatMap((rolls, i) =>
        (rolls || []).map((roll) => ({
          ...roll,
          _inputDate: filteredInputs[i].date,
          _inputId:   filteredInputs[i].id,
          _inputCode: filteredInputs[i].code,
        }))
      );

      // 4. Filtrar por is_void
      const filtered = allRolls.filter((roll) => {
        const isVoid = roll.is_void === true || roll.is_void === 1;
        return reportType === "void" ? isVoid : !isVoid;
      });

      setRolls(filtered);
    } catch {
      setError("Error al obtener los rollos. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Cerrar ── */
  const handleClose = () => {
    setRolls(null);
    setError(null);
    onClose();
  };

  /* ── Atajos de fecha ── */
  const setCurrentMonth = () => {
    setDateFrom(moment().startOf("month"));
    setDateTo(moment().endOf("month"));
    resetResult();
  };
  const setLastMonth = () => {
    const last = moment().subtract(1, "month");
    setDateFrom(last.clone().startOf("month"));
    setDateTo(last.clone().endOf("month"));
    resetResult();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { minHeight: "80vh", display: "flex", flexDirection: "column" } }}
    >
      {/* ── Título ── */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
          color: "#fff",
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PictureAsPdf />
          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "1rem" }}>
            Generar Reporte de Rollos
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, flex: 1, overflow: "auto" }}>
        {/* ── Panel de controles ── */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: "#fdf2f4",
            border: "1px solid rgba(244,63,94,0.18)",
          }}
        >
          {/* Tipo de reporte */}
          <ToggleButtonGroup
            value={reportType}
            exclusive
            onChange={(_, v) => {
              if (v) { setReportType(v); resetResult(); }
            }}
            size="small"
          >
            <ToggleButton
              value="active"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                fontSize: "0.75rem",
                "&.Mui-selected": {
                  background: "rgba(244,63,94,0.12)",
                  color: "#f43f5e",
                  borderColor: "#f43f5e",
                },
              }}
            >
              Entradas Activas
            </ToggleButton>
            <ToggleButton
              value="void"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                fontSize: "0.75rem",
                "&.Mui-selected": {
                  background: "rgba(244,63,94,0.12)",
                  color: "#f43f5e",
                  borderColor: "#f43f5e",
                },
              }}
            >
              Dados de Baja
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Rango de fechas */}
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Desde"
              value={dateFrom}
              onChange={(v) => { setDateFrom(v); resetResult(); }}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", sx: { width: 165 } } }}
            />
            <DatePicker
              label="Hasta"
              value={dateTo}
              onChange={(v) => { setDateTo(v); resetResult(); }}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", sx: { width: 165 } } }}
            />
          </LocalizationProvider>

          {/* Atajos */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<EventNote fontSize="small" />}
            onClick={setCurrentMonth}
            sx={SX_BTN_OUTLINE}
          >
            Mes actual
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EventNote fontSize="small" />}
            onClick={setLastMonth}
            sx={SX_BTN_OUTLINE}
          >
            Mes anterior
          </Button>

          {/* Botón generar */}
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading || !dateFrom || !dateTo}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />
            }
            sx={{
              background: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              "&:hover": { background: "linear-gradient(135deg, #e11d48 0%, #db2777 100%)" },
              "&:disabled": { opacity: 0.6 },
            }}
          >
            {loading ? "Generando..." : "Generar PDF"}
          </Button>

          {/* Chip con resultado */}
          {rolls !== null && (
            <Chip
              label={`${rolls.length} rollo${rolls.length !== 1 ? "s" : ""} encontrado${rolls.length !== 1 ? "s" : ""}`}
              color={rolls.length > 0 ? "success" : "default"}
              size="small"
              sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
            />
          )}
        </Box>

        {/* ── Error ── */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* ── Sin resultados ── */}
        {rolls !== null && rolls.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No se encontraron rollos{" "}
            {reportType === "void" ? "dados de baja" : "activos"} en el período seleccionado.
          </Alert>
        )}

        {/* ── PDF Viewer ── */}
        {rolls !== null && rolls.length > 0 && (
          <InputsReportPdf
            rolls={rolls}
            dateFrom={dateFrom}
            dateTo={dateTo}
            reportType={reportType}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, flexShrink: 0 }}>
        <Button onClick={handleClose} variant="outlined" sx={SX_BTN_OUTLINE}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InputsReportModal;
